/**
 * Schémas Zod — Intake Links (Chunk 9.N.1.A → 9.N.3.A.0).
 *
 * Source DB : `app.intake_links` (migration 0028).
 *
 * Surface privée broker :
 *   - POST   /v1/intake-links            → CreateIntakeLinkSchema           (9.N.1.A)
 *   - PATCH  /v1/intake-links/:id/revoke → RevokeIntakeLinkParamsSchema    (9.N.1.A)
 *   - GET    /v1/intake-links            → IntakeLinkFilterSchema (+ Pagination)
 *                                          → { items: IntakeLinkListItem[], next_cursor }
 *                                          (9.N.3.A.0)
 *
 * Hors scope toujours différé :
 *   - GET /v1/intake-links/:id (détail individuel)         → 9.N.3.B
 *   - filtres avancés (q, sort) / export CSV               → 9.N.3.C
 *   - routes publiques GET/POST /v1/public/intake/:token   → 9.N.1.B (livré)
 *
 * Le token public n'est PAS un schéma Zod côté shared : c'est une string
 * opaque `<id>.<unix_expiry>.<hmac>` produite et vérifiée par
 * `apps/api/src/lib/intake-link-token.ts`. La route GET ne le ré-expose pas
 * (parité POST/api-keys).
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// CREATE — POST /v1/intake-links
// -----------------------------------------------------------------------------

/**
 * Durées de vie autorisées par l'API publique broker. Cf. cadrage F2 §3.2 :
 * « 30 jours par défaut. Broker surcharge 7/30/90/sans (UI 9.N.3).
 *   NULL = jamais. »
 *
 * Exposé en enum strict (vs `z.number().int()` libre) pour éviter qu'un
 * appelant choisisse une valeur arbitraire (1 jour, 365 jours…) avant que
 * la 9.N.3 broker UI fixe formellement le menu déroulant.
 */
export const IntakeLinkExpiresInDaysSchema = z.union([
  z.literal(7),
  z.literal(30),
  z.literal(90),
  z.null(),
]);
export type IntakeLinkExpiresInDays = z.infer<typeof IntakeLinkExpiresInDaysSchema>;

export const CreateIntakeLinkSchema = z
  .object({
    /** UUID du projet broker auquel le lien est rattaché (déterministe — cadrage §3.7). */
    project_id: UuidSchema,
    /**
     * Durée avant expiration en jours. Défaut 30. `null` = pas d'expiration.
     * Voir `IntakeLinkExpiresInDaysSchema`.
     */
    expires_in_days: IntakeLinkExpiresInDaysSchema.optional().default(30),
    /**
     * Quota optionnel de soumissions acceptées via ce lien. `null` ou absent
     * = illimité. Entier ≥ 1 sinon. Cf. `app.intake_links.max_submissions`
     * + CHECK contrainte DB (migration 0028).
     */
    max_submissions: z.number().int().min(1).nullable().optional(),
    /**
     * 9.U.5 — lie le lien à une version de formulaire PUBLIÉE (magic-link
     * versionné). Absent = lien legacy (upload). Présent → validé serveur
     * (existe + même tenant + publiée + template non archivé). Server-authoritative :
     * jamais dérivé du wire au-delà de l'UUID.
     */
    form_template_version_id: UuidSchema.optional(),
    /**
     * Destinataire de demande auquel ce lien est émis — le lien devient
     * **nominatif**, et la provenance de la réponse déterministe PAR
     * CONSTRUCTION (migration 0098).
     *
     * Absent = lien de dossier ordinaire, non nominatif : c'est le cas courant.
     *
     * **Server-authoritative, et jamais fourni par un public** : cette route
     * exige une authentification de cabinet, et l'identifiant est confronté en
     * base — il doit désigner un destinataire de CE dossier, du même tenant,
     * sur une demande active, encore en attente et dont le créneau est libre.
     * Rien de tout cela ne se déduit du fil au-delà de l'UUID.
     */
    quote_request_recipient_id: UuidSchema.optional(),
  })
  .strict();
export type CreateIntakeLinkInput = z.infer<typeof CreateIntakeLinkSchema>;

// -----------------------------------------------------------------------------
// REVOKE — PATCH /v1/intake-links/:id/revoke
// -----------------------------------------------------------------------------

export const RevokeIntakeLinkParamsSchema = z.object({
  id: UuidSchema,
});
export type RevokeIntakeLinkParams = z.infer<typeof RevokeIntakeLinkParamsSchema>;

// -----------------------------------------------------------------------------
// Row (lecture DB)
// -----------------------------------------------------------------------------

export const IntakeLinkRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  created_by: UuidSchema.nullable(),

  /** ISO datetime ou `null` (pas d'expiration). */
  expires_at: z.string().nullable(),
  /** ISO datetime de révocation, ou `null` si actif. */
  revoked_at: z.string().nullable(),

  max_submissions: z.number().int().nullable(),
  submission_count: z.number().int(),

  /** 9.U.5 — version de formulaire liée (magic-link versionné) ou `null` (legacy). */
  form_template_version_id: UuidSchema.nullable(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type IntakeLinkRow = z.infer<typeof IntakeLinkRowSchema>;

// -----------------------------------------------------------------------------
// Bundle de création — payload retourné UNE SEULE FOIS par POST /v1/intake-links
// -----------------------------------------------------------------------------

/**
 * Réponse 201 de POST /v1/intake-links.
 *
 * `intake_token` (opaque `<id>.<unix_expiry>.<hmac>`) et `intake_url` ne sont
 * disponibles QUE dans cette réponse. Aucune route GET ne les ré-expose
 * (parité api-keys.ts §POST). Le broker doit les copier ou régénérer un
 * nouveau lien.
 */
export const IntakeLinkBundleSchema = IntakeLinkRowSchema.extend({
  /** Token public opaque — à transmettre au client final. Non rejouable. */
  intake_token: z.string(),
  /** URL prête à partager : `${PUBLIC_WEB_URL}/intake/<token>`. */
  intake_url: z.string().url(),
});
export type IntakeLinkBundle = z.infer<typeof IntakeLinkBundleSchema>;

// -----------------------------------------------------------------------------
// 9.N.3.A.0 — Listing broker UI : GET /v1/intake-links
// -----------------------------------------------------------------------------

/**
 * Statut dérivé exposé par GET /v1/intake-links pour chaque ligne.
 *
 * Préséance de calcul (côté SQL `CASE WHEN`) — premier match gagne :
 *   1. `revoked`       — `revoked_at IS NOT NULL`
 *   2. `quota_reached` — `revoked_at IS NULL AND max_submissions IS NOT NULL
 *                         AND submission_count >= max_submissions`
 *   3. `expired`       — `revoked_at IS NULL AND expires_at IS NOT NULL
 *                         AND expires_at <= NOW()` (et quota non atteint)
 *   4. `active`        — sinon
 *
 * La préséance reflète la sémantique métier : un lien révoqué reste révoqué
 * même si son expiration ou son quota auraient été atteints sans la révoc.
 * Idem un lien dont le quota est atteint l'emporte sur l'expiration — c'est
 * le motif "actionnable" pour le broker (recréer un lien, ajuster le quota).
 */
export const IntakeLinkStatusSchema = z.enum(["active", "expired", "revoked", "quota_reached"]);
export type IntakeLinkStatus = z.infer<typeof IntakeLinkStatusSchema>;

/**
 * Filtre query `?status=...` sur GET /v1/intake-links.
 * `all` (défaut) = pas de filtre côté SQL — toutes les lignes du tenant.
 */
export const IntakeLinkStatusFilterSchema = z.enum([
  "active",
  "expired",
  "revoked",
  "quota_reached",
  "all",
]);
export type IntakeLinkStatusFilter = z.infer<typeof IntakeLinkStatusFilterSchema>;

/**
 * Filtres query GET /v1/intake-links — combinables avec `PaginationSchema`.
 * Volontairement minimal en 9.N.3.A.0 (cf. décisions PO §5) :
 *   - `status` (défaut `all`)
 *   - `project_id` optionnel pour scoper sur un projet précis
 *
 * `q` (search full-text) est reporté à 9.N.3.C — coût SQL non négligeable
 * sans index dédié et hors scope minimum broker UI.
 */
export const IntakeLinkFilterSchema = z.object({
  status: IntakeLinkStatusFilterSchema.optional().default("all"),
  project_id: UuidSchema.optional(),
});
export type IntakeLinkFilter = z.infer<typeof IntakeLinkFilterSchema>;

/**
 * Résumé projet exposé via JOIN serveur-side (cf. décisions PO §1).
 * On ramène `id` + `title` pour permettre l'affichage broker sans waterfall
 * vers `/v1/projects`. `null` si le projet a été soft-deleté entre-temps
 * (le LEFT JOIN ramène le lien orphelin pour audit broker).
 */
export const IntakeLinkProjectSummarySchema = z.object({
  id: UuidSchema,
  title: z.string(),
});
export type IntakeLinkProjectSummary = z.infer<typeof IntakeLinkProjectSummarySchema>;

/**
 * Item de liste GET /v1/intake-links — `IntakeLinkRowSchema` enrichi de :
 *   - `status` (dérivé SQL — cf. `IntakeLinkStatusSchema`)
 *   - `project` (`{ id, title }` ou `null` si le projet a été soft-deleté)
 *
 * ⚠️ Pas de `intake_token` ni `intake_url` ici — ils ne sont retournés QUE
 * par POST (cf. `IntakeLinkBundleSchema`). Listing = ré-exposition impossible.
 */
export const IntakeLinkListItemSchema = IntakeLinkRowSchema.extend({
  /** Remise XLSX dédiée ; lecture authentifiée seulement, absent sur les anciens serveurs. */
  census_upload_id: UuidSchema.nullable().optional(),
  status: IntakeLinkStatusSchema,
  project: IntakeLinkProjectSummarySchema.nullable(),
  invitation_status: z.enum(["queued", "sent", "failed", "expired"]).nullable().optional(),
  contact: z.object({ id: UuidSchema, name: z.string() }).nullable().optional(),
  /** Identité lisible de la version liée ; absente sur les anciens serveurs. */
  form: z
    .object({
      template_id: UuidSchema,
      name: z.string(),
      version_number: z.number().int().positive(),
      supports_client_signature: z.boolean().optional(),
    })
    .nullable()
    .optional(),
});
export type IntakeLinkListItem = z.infer<typeof IntakeLinkListItemSchema>;
